<template>
	<div>
		<!-- Editor mode -->
		<j-card class="xs-12 bg5">
			<!-- Toolbar -->
			<div class="xs-12 bb1 bc7 p2 pb0">
				<j-button
					v-for="(type, i) in sectionTypes"
					:key="i"
					type="primary"
					size="xsmall"
					class="mr2 mb2"
					@click="addItem(type)"
					>+ {{ type }}</j-button
				>
			</div>
			<!-- Items -->
			<div>
				<div
					class="xs-12"
					:class="{ 'bb1 bc7': i < schema.length - 1 }"
					v-for="(item, i) in schema"
					:key="i"
				>
					<div class="flex xs-12">
						<div class="xs-12">
							<div class="flex xs-between xs-middle">
								<div class="fs3 fw7 p2">{{ item.type }}</div>
								<!-- Actions -->
								<div class="cu2 flex p1 xs-center" @click="removeItem(item)">
									<crm-icon name="delete" color="cError" />
								</div>
							</div>
							<div class="p2 pt0">
								<component
									:is="decideComponent(item.type)"
									size="small"
									:value="item.localizedContent || ''"
									:content="item.localizedContent || ' '"
									:courseId="courseId"
									:lessonId="lesson.id"
									:item="item"
									:inputId="item.id"
									:supportedFileTypes="
										item.type === 'IMAGE'
											? ['jpg', 'jpeg', 'png', 'bmp', 'gif']
											: item.type === 'VIDEO'
											? ['mp4']
											: []
									"
									:selectedLocale="selectedLocale"
									@blur="onBlur(item)"
									@input="onInput(item, $event)"
									@option:added="$emit('item:updated')"
									@option:updated="$emit('item:updated')"
									@option:removed="$emit('item:updated')"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</j-card>

		<j-loading-toast v-if="isLoading" />
	</div>
</template>

<script>
import JButton from "@/components/JButton"
import JCard from "@/components/JCard"
import CrmIcon from "@/components/CrmIcon"
import JBaseInputText from "@/components/JBaseInputText"
import JBaseInputLongText from "@/components/JBaseInputLongText"
import JInputList from "@/components/JInputList"
import CourseEditorFileInput from "./CourseEditorFileInput"
import CourseEditorQuizzInput from "./CourseEditorQuizzInput"
import CourseEditorListInput from "./CourseEditorListInput"
import JLoadingToast from "@/components/JLoadingToast"
import CoreRichTextEditor from "@/modules/core/components/CoreRichTextEditor"

import { checkSuccess } from "../utils/helpers.js"
import api from "@/api/gql.js"
import queries from "../utils/queries.js"
import debounce from "lodash.debounce"

export default {
	name: "LessonEditor",
	props: {
		lesson: Object,
		courseId: String,
		selectedLocale: String
	},
	components: {
		JButton,
		JCard,
		CrmIcon,
		JBaseInputLongText,
		JBaseInputText,
		JInputList,
		CourseEditorFileInput,
		CourseEditorQuizzInput,
		CourseEditorListInput,
		JLoadingToast,
		CoreRichTextEditor
	},
	data() {
		return {
			isLoading: false,
			sectionTypes: [
				"HEADLINE",
				"TEXT",
				"LIST",
				"IMAGE",
				"VIDEO",
				"QUIZZ",
				"HTML"
			]
		}
	},
	computed: {
		schema() {
			return this.lesson?.items || []
		}
	},

	methods: {
		/* We only update items content on blur, not on input */
		onBlur(item) {
			this.updateItem(item, item.localizedContent)
			this.$emit("page:updated")
		},

		/* For HTML items, we cannot mutate item directly or we have the jumping cursor bug
		 * Also we cannot don't have blur event with CoreRichTextEditor
		 * Also we cannot fetch or it will jump the cursor in the beginning
		 * For others, we set it here because if we use v-model it create the jumping cursor bug on HTML items, and it will be updated on blur
		 */
		onInput(item, value) {
			if (item.type === "HTML") {
				return debounce(async () => {
					this.updateItem(item, value)
				}, 200)()
			} else {
				item.localizedContent = value
			}
		},

		decideComponent(type) {
			if (type === "HEADLINE") return "j-base-input-text"
			if (type === "TEXT") return "j-base-input-long-text"
			if (type === "LIST") return "course-editor-list-input"
			if (type === "IMAGE") return "course-editor-file-input"
			if (type === "VIDEO") return "course-editor-file-input"
			if (type === "QUIZZ") return "course-editor-quizz-input"
			if (type === "HTML") return "core-rich-text-editor"
			return null
		},

		async addItem(type) {
			if (this.isLoading) return
			this.isLoading = true
			let variables = {
				courseId: this.courseId,
				lessonId: this.lesson.id,
				input: {
					type
				}
			}

			let res = await api({ query: queries.addLessonItem, variables })

			this.isLoading = false
			if (
				!checkSuccess(
					res,
					"addLessonItem",
					this.$t("errors.general"),
					this.$store
				)
			)
				return
			this.$emit("item:added")
		},

		async updateItem(item, value) {
			if (this.isLoading) return
			this.isLoading = true
			const variables = {
				l10n: {
					key: `lessonItems-${item.id}-content`,
					locale: this.selectedLocale,
					value: value || ""
				}
			}

			const res = await api({ query: queries.upsertL10n, variables })
			this.isLoading = false
			if (
				!checkSuccess(res, "upsertL10n", this.$t("errors.general"), this.$store)
			)
				return
		},

		async removeItem(item) {
			if (this.isLoading) return
			this.isLoading = true
			// Remove item from lesson
			let variables = {
				courseId: this.courseId,
				lessonId: this.lesson.id,
				itemId: item.id
			}

			let res = await api({ query: queries.removeLessonItem, variables })

			if (
				!checkSuccess(
					res,
					"removeLessonItem",
					this.$t("errors.general"),
					this.$store
				)
			) {
				this.isLoading = false
				return
			}

			// Delete l10n localizedContent
			variables = {
				key: `lessonItems-${item.id}-content`
			}
			res = await api({ query: queries.deleteAllL10n, variables })

			// If QUIZZ or LIST, need to deleteAllL10n for all the options too
			if (item.type === "QUIZZ" || item.type === "LIST") {
				item.options.forEach(async opt => {
					variables = {
						key: `lessonItemsOptions-${opt.id}-content`
					}
					res = await api({ query: queries.deleteAllL10n, variables })
				})
			}
			this.isLoading = false
			this.$emit("item:removed")
		}
	}
}
</script>
